// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
window.THREE = THREE;
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const ITEMS_DB = {
  broyeur: {
    name: "Vieux Broyeur",
    icon: "⚙️",
    desc: "Un ancien broyeur à mâchoires rouillé",
  },
  four_rotatif: {
    name: "Four Rotatif",
    icon: "🔥",
    desc: "Le cœur de l'ancienne cimenterie",
  },
  tapis_roulant: {
    name: "Tapis Roulant",
    icon: "🔗",
    desc: "Un convoyeur à bande usé",
  },
  silo: {
    name: "Pièces de Silo",
    icon: "🏗️",
    desc: "Plaques métalliques d'un ancien silo",
  },
  cables: {
    name: "Câbles Cuivre",
    icon: "🔌",
    desc: "Câbles électriques récupérables",
  },
  engrenages: {
    name: "Engrenages",
    icon: "⛓️",
    desc: "Engrenages de différentes tailles",
  },
  toles: {
    name: "Tôles Ondulées",
    icon: "🪨",
    desc: "Tôles encore en bon état",
  },
  briques: {
    name: "Briques Réfractaires",
    icon: "🧱",
    desc: "Briques résistantes à la chaleur",
  },
};

const CRAFTS_DB = {
  table_industrielle: {
    name: "Table Industrielle",
    icon: "🪑",
    needs: ["broyeur", "engrenages"],
    visitors: 5,
    desc: "Table design style loft",
    pos: [6, 0, -3],
  },
  sculpture_metal: {
    name: "Sculpture Métal",
    icon: "🗿",
    needs: ["broyeur", "cables"],
    visitors: 15,
    desc: "Œuvre d'art en métal recyclé",
    pos: [0, 0, -20],
  },
  four_pizza: {
    name: "Four à Pizza",
    icon: "🍕",
    needs: ["four_rotatif", "briques"],
    visitors: 25,
    desc: "Four artisanal pour pizzas",
    pos: [-8, 0, 18],
  },
  barbecue_geant: {
    name: "Barbecue Géant",
    icon: "🥩",
    needs: ["four_rotatif", "toles"],
    visitors: 20,
    desc: "Espace barbecue communautaire",
    pos: [10, 0, 20],
  },
  banc_public: {
    name: "Banc Public",
    icon: "💺",
    needs: ["tapis_roulant", "toles"],
    visitors: 5,
    desc: "Banc design upcyclé",
    pos: [-5, 0, -10],
  },
  etagere_deco: {
    name: "Étagère Déco",
    icon: "📚",
    needs: ["tapis_roulant", "cables"],
    visitors: 8,
    desc: "Étagère industrielle tendance",
    pos: [8, 0, 5],
  },
  luminaire: {
    name: "Luminaire Design",
    icon: "💡",
    needs: ["cables", "engrenages"],
    visitors: 10,
    desc: "Lampe style factory",
    pos: [-3, 0, 0],
  },
  horloge_geante: {
    name: "Horloge Géante",
    icon: "🕐",
    needs: ["engrenages", "toles"],
    visitors: 12,
    desc: "Horloge murale spectaculaire",
    pos: [0, 0, -23],
  },
  kiosque: {
    name: "Kiosque d'Accueil",
    icon: "🏪",
    needs: ["silo", "toles"],
    visitors: 20,
    desc: "Point info pour visiteurs",
    pos: [0, 0, 28],
  },
  jardiniere: {
    name: "Jardinière Géante",
    icon: "🌿",
    needs: ["silo", "briques"],
    visitors: 10,
    desc: "Bac végétalisé XXL",
    pos: [-10, 0, 22],
  },
  mur_escalade: {
    name: "Mur d'Escalade",
    icon: "🧗",
    needs: ["briques", "cables"],
    visitors: 30,
    desc: "Attraction sportive",
    pos: [14, 0, -20],
  },
  abri_jardin: {
    name: "Abri de Jardin",
    icon: "🏡",
    needs: ["toles", "briques"],
    visitors: 8,
    desc: "Petit abri pour le jardin",
    pos: [-12, 0, 15],
  },
};

const COLLECTIBLES = [
  { id: "broyeur", pos: [12, 1, -8], color: 0x8b4513 },
  { id: "four_rotatif", pos: [-15, 1, 5], color: 0xcc4400 },
  { id: "tapis_roulant", pos: [5, 0.5, 15], color: 0x556b2f },
  { id: "silo", pos: [-8, 1, -18], color: 0x708090 },
  { id: "cables", pos: [18, 0.8, 8], color: 0xb87333 },
  { id: "engrenages", pos: [-12, 0.6, -5], color: 0x696969 },
  { id: "toles", pos: [8, 0.7, -15], color: 0x808080 },
  { id: "briques", pos: [-5, 0.5, 20], color: 0xa0522d },
];

function buildCraftModel(id) {
  const g = new THREE.Group();
  const cm = (c, r = 0.5) =>
    new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: 0.3 });
  const mm = (c) =>
    new THREE.MeshStandardMaterial({
      color: c,
      roughness: 0.3,
      metalness: 0.7,
    });
  const add = (geo, mat, x, y, z, rx, ry, rz) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    if (rx) m.rotation.x = rx;
    if (ry) m.rotation.y = ry;
    if (rz) m.rotation.z = rz;
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
    return m;
  };

  switch (id) {
    case "table_industrielle":
      add(new THREE.BoxGeometry(2, 0.1, 1), mm(0x555555), 0, 1, 0);
      for (let lx of [-0.8, 0.8])
        for (let lz of [-0.4, 0.4])
          add(
            new THREE.CylinderGeometry(0.06, 0.06, 1, 8),
            mm(0x8b4513),
            lx,
            0.5,
            lz
          );
      add(
        new THREE.TorusGeometry(0.2, 0.03, 6, 8),
        mm(0x777777),
        0,
        1.1,
        0,
        Math.PI / 2
      );
      break;
    case "sculpture_metal":
      add(new THREE.BoxGeometry(0.3, 3, 0.3), mm(0xaa5533), 0, 1.5, 0);
      add(new THREE.SphereGeometry(0.5, 12, 12), mm(0xcc6644), 0, 3.2, 0);
      add(
        new THREE.TorusGeometry(0.6, 0.08, 8, 16),
        mm(0xb87333),
        0,
        2,
        0,
        0.5,
        0.3
      );
      add(
        new THREE.TorusGeometry(0.4, 0.06, 8, 16),
        mm(0x996644),
        0,
        1.2,
        0,
        -0.3,
        0.8
      );
      add(
        new THREE.CylinderGeometry(0.6, 0.7, 0.2, 16),
        cm(0x444444),
        0,
        0.1,
        0
      );
      {
        const sl = new THREE.SpotLight(0xffdd88, 1, 8, 0.4);
        sl.position.set(0, 5, 0);
        sl.target.position.set(0, 0, 0);
        g.add(sl);
        g.add(sl.target);
      }
      break;
    case "four_pizza":
      add(
        new THREE.SphereGeometry(1, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2),
        cm(0xcc5500, 0.7),
        0,
        0.8,
        0
      );
      add(
        new THREE.CylinderGeometry(1.2, 1.3, 0.8, 16),
        cm(0xa0522d),
        0,
        0.4,
        0
      );
      add(new THREE.BoxGeometry(0.6, 0.5, 0.3), cm(0x222222), 0, 0.9, 1.05);
      add(
        new THREE.CylinderGeometry(0.15, 0.2, 0.8, 8),
        cm(0x8b4513),
        0,
        1.8,
        -0.3
      );
      {
        const fl = new THREE.PointLight(0xff4400, 0.8, 4);
        fl.position.set(0, 0.8, 0.5);
        g.add(fl);
      }
      break;
    case "barbecue_geant":
      add(new THREE.BoxGeometry(2.5, 0.8, 1.2), mm(0x333333), 0, 1, 0);
      for (let lx of [-1, 1])
        for (let lz of [-0.4, 0.4])
          add(
            new THREE.CylinderGeometry(0.06, 0.06, 1, 8),
            mm(0x555555),
            lx,
            0.5,
            lz
          );
      for (let i = -0.9; i <= 0.9; i += 0.3)
        add(
          new THREE.CylinderGeometry(0.02, 0.02, 1, 6),
          mm(0x888888),
          i,
          1.42,
          0,
          0,
          0,
          Math.PI / 2
        );
      {
        const el = new THREE.PointLight(0xff2200, 0.5, 3);
        el.position.set(0, 1.2, 0);
        g.add(el);
      }
      break;
    case "banc_public":
      add(new THREE.BoxGeometry(2, 0.12, 0.6), cm(0x8b6914), 0, 0.55, 0);
      add(new THREE.BoxGeometry(2, 0.6, 0.1), cm(0x8b6914), 0, 0.95, -0.3);
      for (let x of [-0.8, 0.8]) {
        add(new THREE.BoxGeometry(0.08, 0.55, 0.6), mm(0x555555), x, 0.28, 0);
        add(
          new THREE.BoxGeometry(0.08, 0.65, 0.08),
          mm(0x555555),
          x,
          0.73,
          -0.3
        );
      }
      break;
    case "etagere_deco":
      for (let x of [-0.6, 0.6])
        add(new THREE.BoxGeometry(0.08, 2.2, 0.4), mm(0x555555), x, 1.1, 0);
      for (let y = 0.4; y <= 2; y += 0.5)
        add(new THREE.BoxGeometry(1.3, 0.06, 0.4), cm(0x8b6914), 0, y, 0);
      add(new THREE.BoxGeometry(0.2, 0.3, 0.15), cm(0xcc4444), -0.3, 0.58, 0);
      add(
        new THREE.CylinderGeometry(0.1, 0.1, 0.25, 8),
        cm(0x44aa44),
        0.2,
        0.53,
        0
      );
      add(new THREE.SphereGeometry(0.1, 8, 8), mm(0xb87333), 0, 1.05, 0);
      break;
    case "luminaire":
      add(
        new THREE.CylinderGeometry(0.05, 0.08, 3, 8),
        mm(0x444444),
        0,
        1.5,
        0
      );
      add(new THREE.ConeGeometry(0.5, 0.4, 12), mm(0xb87333), 0, 3.1, 0);
      add(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xffee88 }),
        0,
        2.85,
        0
      );
      add(
        new THREE.CylinderGeometry(0.3, 0.35, 0.1, 12),
        mm(0x555555),
        0,
        0.05,
        0
      );
      {
        const ll = new THREE.PointLight(0xffdd66, 1.5, 8);
        ll.position.set(0, 2.8, 0);
        g.add(ll);
      }
      break;
    case "horloge_geante":
      add(
        new THREE.CylinderGeometry(1.5, 1.5, 0.15, 32),
        mm(0x555555),
        0,
        3,
        -24.7
      );
      add(
        new THREE.CylinderGeometry(1.3, 1.3, 0.05, 32),
        cm(0xeeddcc, 0.8),
        0,
        3,
        -24.5
      );
      add(new THREE.BoxGeometry(0.08, 0.8, 0.03), mm(0x222222), 0, 3.35, -24.4);
      add(
        new THREE.BoxGeometry(0.05, 1.1, 0.03),
        mm(0x333333),
        0.2,
        3.4,
        -24.38,
        0,
        0,
        -0.8
      );
      add(
        new THREE.TorusGeometry(0.15, 0.04, 6, 8),
        mm(0x8b4513),
        0,
        3,
        -24.35
      );
      add(new THREE.SphereGeometry(0.08, 8, 8), mm(0xffdd44), 0, 3, -24.38);
      break;
    case "kiosque":
      for (let x of [-1.5, 1.5])
        for (let z of [-1, 1])
          add(
            new THREE.CylinderGeometry(0.12, 0.12, 3, 8),
            mm(0x708090),
            x,
            1.5,
            z
          );
      add(new THREE.BoxGeometry(3.5, 0.15, 2.5), mm(0x556b2f), 0, 3.05, 0);
      add(new THREE.BoxGeometry(3, 0.1, 1), cm(0x8b6914), 0, 1.1, 0.8);
      add(new THREE.BoxGeometry(2, 0.5, 0.05), cm(0xddccaa), 0, 3.5, 0);
      {
        const kl = new THREE.PointLight(0xffeecc, 0.8, 6);
        kl.position.set(0, 2.8, 0);
        g.add(kl);
      }
      break;
    case "jardiniere":
      add(new THREE.BoxGeometry(2, 1, 1.5), cm(0x8b4513), 0, 0.5, 0);
      add(new THREE.BoxGeometry(1.8, 0.1, 1.3), cm(0x3d2817), 0, 1.02, 0);
      for (let i = 0; i < 5; i++)
        add(
          new THREE.SphereGeometry(0.15 + Math.random() * 0.2, 8, 8),
          cm(0x228b22),
          -0.6 + Math.random() * 1.2,
          1.3,
          -0.4 + Math.random() * 0.8
        );
      for (let i = 0; i < 3; i++)
        add(
          new THREE.SphereGeometry(0.08, 6, 6),
          new THREE.MeshBasicMaterial({
            color: [0xff6688, 0xffdd44, 0xff88cc][i],
          }),
          -0.4 + i * 0.4,
          1.55,
          0
        );
      break;
    case "mur_escalade":
      add(new THREE.BoxGeometry(4, 5, 0.3), cm(0x8e8e7e), 0, 2.5, 0);
      for (let i = 0; i < 18; i++)
        add(
          new THREE.SphereGeometry(0.1, 8, 8),
          cm([0xff4444, 0x44ff44, 0x4444ff, 0xffff44, 0xff8800][i % 5], 0.6),
          -1.5 + Math.random() * 3,
          0.5 + Math.random() * 4,
          0.2
        );
      add(new THREE.BoxGeometry(4.5, 0.15, 2), cm(0x4444aa, 0.9), 0, 0.08, 1.2);
      break;
    case "abri_jardin":
      add(new THREE.BoxGeometry(2.5, 2, 0.1), cm(0x808080), 0, 1, -1);
      add(new THREE.BoxGeometry(0.1, 2, 2), cm(0x888888), -1.25, 1, 0);
      add(new THREE.BoxGeometry(0.1, 2, 2), cm(0x888888), 1.25, 1, 0);
      {
        const r = add(
          new THREE.BoxGeometry(2.8, 0.08, 2.4),
          mm(0x707070),
          0,
          2.2,
          0
        );
        r.rotation.x = 0.15;
      }
      add(new THREE.BoxGeometry(0.8, 1.6, 0.05), cm(0x5c4033), 0, 0.8, 1.01);
      {
        const al = new THREE.PointLight(0xffeeaa, 0.4, 4);
        al.position.set(0, 1.8, 0);
        g.add(al);
      }
      break;
  }

  // Floating green indicator ring
  const indicator = new THREE.Mesh(
    new THREE.TorusGeometry(0.3, 0.02, 6, 16),
    new THREE.MeshBasicMaterial({
      color: 0x44ff88,
      transparent: true,
      opacity: 0.7,
    })
  );
  indicator.position.y = 4;
  indicator.rotation.x = Math.PI / 2;
  g.add(indicator);
  g.userData.indicator = indicator;
  return g;
}

export default function CementFactoryGame() {
  const canvasRef = useRef(null);
  const playerRef = useRef({ x: 0, y: 2, z: 0, rotY: 0, rotX: 0 });
  const keysRef = useRef(new Set());
  const dragRef = useRef({ active: false, lx: 0, ly: 0 });
  const collectiblesRef = useRef([]);
  const craftModelsRef = useRef({});
  const gltfLoaderRef = useRef(null);
  const sceneRef = useRef(null);
  const rafRef = useRef(null);

  const [inventory, setInventory] = useState({});
  const [crafted, setCrafted] = useState({});
  const [visitors, setVisitors] = useState(0);
  const [renovationLevel, setRenovationLevel] = useState(0);
  const [showCraft, setShowCraft] = useState(false);
  const [showInv, setShowInv] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [nearItem, setNearItem] = useState(null);
  const [nearCraft, setNearCraft] = useState(null);
  const [started, setStarted] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const glbModelRef = useRef(null);

  // Load GLB model on mount
  useEffect(() => {
    gltfLoaderRef.current = new GLTFLoader(); // On utilise le chargeur importé proprement !

    gltfLoaderRef.current.load(
      "./ma-sculpture.glb", // <-- N'oubliez pas de mettre le vrai nom de votre fichier !
      (gltf) => {
        const model = gltf.scene;

        // Centre automatiquement le modèle
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        const wrapper = new THREE.Group();
        wrapper.add(model);

        // ↕️ AJUSTEZ LA HAUTEUR ICI :
        // "+=" le fait monter, "-=" le fait descendre
        model.position.y += 1;

        // 🎛️ REGLAGES DE VOTRE MODÈLE :
        wrapper.scale.set(2, 2, 2);

        // Gère les ombres de votre modèle
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        // On stocke le modèle en mémoire pour que le jeu l'utilise lors du Craft !
        glbModelRef.current = wrapper;
      },
      undefined,
      (err) => console.error("Erreur lors du chargement :", err)
    );
  }, []);

  const notify = useCallback((msg, type = "info") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 2500);
  }, []);

  const craftItem = useCallback(
    (id) => {
      const r = CRAFTS_DB[id];
      if (!r) return;
      setInventory((prev) => {
        const ok = r.needs.every((n) => (prev[n] || 0) > 0);
        if (!ok) return prev;
        const next = { ...prev };
        r.needs.forEach((n) => {
          next[n]--;
          if (next[n] <= 0) delete next[n];
        });
        setCrafted((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
        setVisitors((v) => {
          const nv = v + r.visitors;
          setRenovationLevel(Math.min(100, Math.floor(nv / 2)));
          return nv;
        });
        notify(`${r.icon} ${r.name} créé ! +${r.visitors} visiteurs`, "craft");
        return next;
      });
    },
    [notify]
  );

  const doCollect = useCallback(() => {
    const p = playerRef.current;
    const item = collectiblesRef.current.find((c) => {
      if (c.userData.collected) return false;
      return Math.hypot(p.x - c.position.x, p.z - c.position.z) < 3;
    });
    if (item) {
      item.userData.collected = true;
      item.visible = false;
      const iid = item.userData.itemId;
      setInventory((prev) => ({ ...prev, [iid]: (prev[iid] || 0) + 1 }));
      notify(
        `${ITEMS_DB[iid]?.icon} ${ITEMS_DB[iid]?.name} récupéré !`,
        "collect"
      );
      setTimeout(() => {
        item.userData.collected = false;
        item.visible = true;
      }, 30000);
    }
  }, [notify]);

  const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.warn("Erreur plein écran:", err);
        });
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    };

  // Spawn craft models when crafted changes
  useEffect(() => {
    if (!sceneRef.current) return;
    Object.keys(crafted).forEach((id) => {
      if (craftModelsRef.current[id]) return;
      const recipe = CRAFTS_DB[id];
      if (!recipe) return;

      let model;
      if (id === "sculpture_metal" && glbModelRef.current) {
        // Use the embedded GLB model
        model = glbModelRef.current.clone();
        // Add indicator ring
        const indicator = new THREE.Mesh(
          new THREE.TorusGeometry(0.3, 0.02, 6, 16),
          new THREE.MeshBasicMaterial({
            color: 0x44ff88,
            transparent: true,
            opacity: 0.7,
          })
        );
        indicator.position.y = 4;
        indicator.rotation.x = Math.PI / 2;
        model.add(indicator);
        model.userData.indicator = indicator;
      } else {
        model = buildCraftModel(id);
      }

      model.position.set(recipe.pos[0], recipe.pos[1], recipe.pos[2]);
      model.userData.craftId = id;
      sceneRef.current.add(model);
      craftModelsRef.current[id] = model;
    });
  }, [crafted]);

  // THREE.JS SCENE
  useEffect(() => {
    if (!started || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const W = canvas.parentElement.clientWidth,
      H = canvas.parentElement.clientHeight;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.85;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);
    scene.fog = new THREE.FogExp2(0x9db8c7, 0.012);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(70, W / H, 0.1, 200);

    // --- NOUVEAU : LAMPE TORCHE ---
    // SpotLight(couleur, intensité, distance, angle, pénombre, dégradation)
    const flashlight = new THREE.SpotLight(
      0xffffee,
      5,
      60,
      Math.PI / 7,
      0.5,
      1
    );

    // On la place légèrement en dessous et à droite de la caméra (comme tenue en main)
    flashlight.position.set(0, 0, 0);
    flashlight.target.position.set(0, 0, -1); // Elle pointe droit devant

    // On attache la lumière et sa cible à la caméra
    camera.add(flashlight);
    camera.add(flashlight.target);

    // TRÈS IMPORTANT : On ajoute la caméra à la scène pour que la lumière existe !
    scene.add(camera);
    // ------------------------------

    scene.add(new THREE.AmbientLight(0x8899aa, 0.8));
    const sun = new THREE.DirectionalLight(0xffeecc, 1.2);
    sun.position.set(30, 40, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    scene.add(sun);
    scene.add(new THREE.HemisphereLight(0x88aacc, 0x445522, 0.4));

    const cMat = (c, r = 0.9) =>
      new THREE.MeshStandardMaterial({
        color: c,
        roughness: r,
        metalness: 0.1,
      });
    const mMat = (c) =>
      new THREE.MeshStandardMaterial({
        color: c,
        roughness: 0.4,
        metalness: 0.7,
      });
    const rustMat = new THREE.MeshStandardMaterial({
      color: 0x8b4513,
      roughness: 0.8,
      metalness: 0.3,
    });
    const addBox = (w, h, d, mat, x, y, z) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      m.castShadow = true;
      m.receiveShadow = true;
      scene.add(m);
      return m;
    };

    // Ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(100, 100),
      cMat(0x7a7a6a, 0.95)
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    // Walls
    addBox(30, 8, 0.5, cMat(0x9e9e8e), 0, 4, -25);
    addBox(30, 8, 0.5, cMat(0x9e9e8e), 0, 4, 25);
    addBox(0.5, 8, 50, cMat(0x8e8e7e), -15, 4, 0);
    addBox(0.5, 8, 50, cMat(0x8e8e7e), 15, 4, 0);
    for (let i = -12; i <= 12; i += 8)
      addBox(7, 0.3, 50, cMat(0x7a7a6a), i, 8, 0);

    // Silos
    for (let i = 0; i < 3; i++) {
      const s = new THREE.Mesh(
        new THREE.CylinderGeometry(3, 3, 12, 16),
        cMat(0xa0a090)
      );
      s.position.set(-25 + i * 7, 6, -15);
      s.castShadow = true;
      scene.add(s);
      const c = new THREE.Mesh(
        new THREE.ConeGeometry(3.2, 2, 16),
        mMat(0x606060)
      );
      c.position.set(-25 + i * 7, 13, -15);
      scene.add(c);
    }

    // Conveyor
    for (let i = 0; i < 8; i++)
      addBox(
        0.3,
        3 + i * 0.5,
        0.3,
        mMat(0x555555),
        -10 + i * 4,
        (3 + i * 0.5) / 2,
        10
      );
    const belt = new THREE.Mesh(new THREE.BoxGeometry(30, 0.1, 1.5), rustMat);
    belt.position.set(3, 4, 10);
    belt.rotation.z = Math.atan2(2, 30);
    scene.add(belt);

    // Kiln
    const kiln = new THREE.Mesh(
      new THREE.CylinderGeometry(2.5, 2.5, 15, 16),
      mMat(0x8b0000)
    );
    kiln.rotation.z = Math.PI / 2;
    kiln.position.set(-15, 3, 5);
    kiln.castShadow = true;
    scene.add(kiln);
    for (let i = -2; i <= 2; i++)
      addBox(0.4, 2, 3, cMat(0x808080), -15 + i * 3, 1, 5);

    // Crusher
    addBox(4, 3, 4, mMat(0x4a3728), 12, 1.5, -8);
    addBox(3, 2, 3, mMat(0x5c4033), 12, 4, -8);
    const hop = new THREE.Mesh(
      new THREE.CylinderGeometry(1.5, 0.5, 3, 8),
      mMat(0x696969)
    );
    hop.position.set(12, 6, -8);
    scene.add(hop);

    // Debris
    for (let i = 0; i < 40; i++) {
      const s = 0.2 + Math.random() * 0.8;
      addBox(
        s,
        s * 0.5,
        s,
        cMat(0x808070 + Math.floor(Math.random() * 0x202020)),
        (Math.random() - 0.5) * 28,
        s * 0.25,
        (Math.random() - 0.5) * 48
      );
    }

    // Pipes
    for (let z = -24; z <= 24; z += 3) {
      const p = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15, 0.15, 8, 8),
        mMat(0x777777)
      );
      p.position.set(-14.5, 4, z);
      scene.add(p);
    }
    const bp = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 30, 12),
      rustMat
    );
    bp.rotation.z = Math.PI / 2;
    bp.position.set(0, 6.5, -5);
    scene.add(bp);

    // Windows
    for (let z = -20; z <= 20; z += 10) {
      addBox(0.6, 2, 3, cMat(0x6a6a5a), 15.1, 5, z);
      const beam = new THREE.Mesh(
        new THREE.PlaneGeometry(3, 8),
        new THREE.MeshBasicMaterial({
          color: 0xffffcc,
          transparent: true,
          opacity: 0.04,
          side: THREE.DoubleSide,
        })
      );
      beam.position.set(12, 4, z);
      beam.rotation.y = Math.PI / 2;
      beam.rotation.z = 0.3;
      scene.add(beam);
    }

    // Control room
    addBox(6, 4, 0.3, cMat(0xa0a090), 20, 2, -12);
    addBox(0.3, 4, 8, cMat(0x909080), 17, 2, -16);
    addBox(0.3, 4, 8, cMat(0x909080), 23, 2, -16);

    // Dust
    const pGeo = new THREE.BufferGeometry();
    const pArr = new Float32Array(500 * 3);
    for (let i = 0; i < 1500; i += 3) {
      pArr[i] = (Math.random() - 0.5) * 40;
      pArr[i + 1] = Math.random() * 8;
      pArr[i + 2] = (Math.random() - 0.5) * 50;
    }
    pGeo.setAttribute("position", new THREE.BufferAttribute(pArr, 3));
    scene.add(
      new THREE.Points(
        pGeo,
        new THREE.PointsMaterial({
          color: 0xccbb99,
          size: 0.05,
          transparent: true,
          opacity: 0.6,
        })
      )
    );

    // Placement preview markers (green rings on ground)
    Object.entries(CRAFTS_DB).forEach(([, recipe]) => {
      const marker = new THREE.Mesh(
        new THREE.RingGeometry(0.8, 1, 16),
        new THREE.MeshBasicMaterial({
          color: 0x44ff88,
          transparent: true,
          opacity: 0.15,
          side: THREE.DoubleSide,
        })
      );
      marker.position.set(recipe.pos[0], 0.05, recipe.pos[2]);
      marker.rotation.x = -Math.PI / 2;
      scene.add(marker);
    });

    // Collectibles
    const colls = [];
    COLLECTIBLES.forEach((cfg) => {
      const gr = new THREE.Group();
      gr.add(
        new THREE.Mesh(
          new THREE.SphereGeometry(0.5, 16, 16),
          new THREE.MeshStandardMaterial({
            color: cfg.color,
            emissive: cfg.color,
            emissiveIntensity: 0.5,
            roughness: 0.3,
            metalness: 0.5,
          })
        )
      );
      gr.add(
        new THREE.Mesh(
          new THREE.TorusGeometry(0.7, 0.05, 8, 32),
          new THREE.MeshBasicMaterial({ color: 0xffdd44 })
        )
      );
      gr.add(new THREE.PointLight(cfg.color, 0.5, 5));
      gr.position.set(...cfg.pos);
      gr.userData = { itemId: cfg.id, collected: false };
      scene.add(gr);
      colls.push(gr);
    });
    collectiblesRef.current = colls;

    // GAME LOOP
    const clock = new THREE.Clock();
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const dt = Math.min(clock.getDelta(), 0.05);
      const p = playerRef.current;
      const keys = keysRef.current;
      const t = clock.elapsedTime;

      const speed = keys.has("ShiftLeft") || keys.has("ShiftRight") ? 12 : 6;
      let mx = 0,
        mz = 0;
      if (keys.has("KeyW") || keys.has("KeyZ") || keys.has("ArrowUp")) mz = -1;
      if (keys.has("KeyS") || keys.has("ArrowDown")) mz = 1;
      if (keys.has("KeyA") || keys.has("KeyQ") || keys.has("ArrowLeft"))
        mx = -1;
      if (keys.has("KeyD") || keys.has("ArrowRight")) mx = 1;
      if (mx || mz) {
        const len = Math.sqrt(mx * mx + mz * mz);
        mx /= len;
        mz /= len;
        const cos = Math.cos(p.rotY),
          sin = Math.sin(p.rotY);
        p.x += (mx * cos + mz * sin) * speed * dt;
        p.z += (-mx * sin + mz * cos) * speed * dt;
        p.x = Math.max(-48, Math.min(48, p.x));
        p.z = Math.max(-48, Math.min(48, p.z));
      }

      camera.position.set(p.x, p.y, p.z);
      camera.rotation.order = "YXZ";
      camera.rotation.y = p.rotY;
      camera.rotation.x = p.rotX;

      // Collectibles
      let closestId = null,
        closestDist = 999;
      colls.forEach((c) => {
        if (c.userData.collected) return;
        c.children[0].position.y = Math.sin(t * 2 + c.position.x) * 0.2;
        c.children[1].rotation.x = t * 2;
        c.children[1].rotation.y = t;
        const d = Math.hypot(p.x - c.position.x, p.z - c.position.z);
        if (d < 3 && d < closestDist) {
          closestDist = d;
          closestId = c.userData.itemId;
        }
      });
      setNearItem(closestId);

      // Craft models proximity + animation
      let cCraft = null,
        cCraftDist = 999;
      Object.entries(craftModelsRef.current).forEach(([id, model]) => {
        if (model.userData.indicator) {
          model.userData.indicator.rotation.z = t * 0.5;
          model.userData.indicator.position.y = 4 + Math.sin(t * 1.5) * 0.15;
        }
        const d = Math.hypot(p.x - model.position.x, p.z - model.position.z);
        if (d < 4 && d < cCraftDist) {
          cCraftDist = d;
          cCraft = id;
        }
      });
      setNearCraft(cCraft);

      // Dust
      const arr = pGeo.attributes.position.array;
      for (let i = 0; i < arr.length; i += 3) {
        arr[i] += Math.sin(t + i) * 0.002;
        arr[i + 1] += Math.cos(t * 0.5 + i) * 0.001;
        if (arr[i + 1] > 8) arr[i + 1] = 0;
      }
      pGeo.attributes.position.needsUpdate = true;

      renderer.render(scene, camera);
    };
    loop();

    const onResize = () => {
      const w = canvas.parentElement.clientWidth,
        h = canvas.parentElement.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      sceneRef.current = null;
      craftModelsRef.current = {};
    };
  }, [started]);

  // KEYBOARD
  useEffect(() => {
    if (!started) return;
    const down = (e) => {
      keysRef.current.add(e.code);
      if (e.code === "KeyE") doCollect();
      if (e.code === "KeyF") toggleFullscreen();
      if (e.code === "KeyI") setShowInv((p) => !p);
      if (e.code === "KeyC") setShowCraft((p) => !p);
      if (e.code === "KeyH") setShowHelp((p) => !p);
      if (e.code === "Escape") {
        setShowCraft(false);
        setShowInv(false);
        setShowHelp(false);
      }
    };
    const up = (e) => keysRef.current.delete(e.code);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [started, doCollect]);

  // MOUSE DRAG
  useEffect(() => {
    if (!started) return;
    const onDown = (e) => {
      if (e.target.closest("button") || e.target.closest("[data-panel]"))
        return;
      dragRef.current = { active: true, lx: e.clientX, ly: e.clientY };
    };
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const dx = e.clientX - dragRef.current.lx,
        dy = e.clientY - dragRef.current.ly;
      dragRef.current.lx = e.clientX;
      dragRef.current.ly = e.clientY;
      playerRef.current.rotY -= dx * 0.004;
      playerRef.current.rotX = Math.max(
        -1.2,
        Math.min(1.2, playerRef.current.rotX - dy * 0.004)
      );
    };
    const onUp = () => {
      dragRef.current.active = false;
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [started]);

  // TOUCH
  useEffect(() => {
    if (!started) return;
    let moveId = null,
      lookId = null,
      ms = null,
      ll = null;
    const onTS = (e) => {
      for (const t of e.changedTouches) {
        const half = window.innerWidth / 2;
        if (t.clientX < half && moveId === null) {
          moveId = t.identifier;
          ms = { x: t.clientX, y: t.clientY };
        } else if (t.clientX >= half && lookId === null) {
          lookId = t.identifier;
          ll = { x: t.clientX, y: t.clientY };
        }
      }
    };
    const onTM = (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier === moveId && ms) {
          const dx = t.clientX - ms.x,
            dy = t.clientY - ms.y;
          const k = keysRef.current;
          dy < -15 ? k.add("KeyW") : k.delete("KeyW");
          dy > 15 ? k.add("KeyS") : k.delete("KeyS");
          dx < -15 ? k.add("KeyA") : k.delete("KeyA");
          dx > 15 ? k.add("KeyD") : k.delete("KeyD");
        }
        if (t.identifier === lookId && ll) {
          const dx = t.clientX - ll.x,
            dy = t.clientY - ll.y;
          ll = { x: t.clientX, y: t.clientY };
          playerRef.current.rotY -= dx * 0.005;
          playerRef.current.rotX = Math.max(
            -1.2,
            Math.min(1.2, playerRef.current.rotX - dy * 0.005)
          );
        }
      }
    };
    const onTE = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === moveId) {
          moveId = null;
          ms = null;
          keysRef.current.delete("KeyW");
          keysRef.current.delete("KeyS");
          keysRef.current.delete("KeyA");
          keysRef.current.delete("KeyD");
        }
        if (t.identifier === lookId) {
          lookId = null;
          ll = null;
        }
      }
    };
    window.addEventListener("touchstart", onTS, { passive: false });
    window.addEventListener("touchmove", onTM, { passive: false });
    window.addEventListener("touchend", onTE);
    return () => {
      window.removeEventListener("touchstart", onTS);
      window.removeEventListener("touchmove", onTM);
      window.removeEventListener("touchend", onTE);
    };
  }, [started]);

  const invCount = Object.values(inventory).reduce((a, b) => a + b, 0);
  const craftedCount = Object.values(crafted).reduce((a, b) => a + b, 0);
  const anyPanel = showCraft || showInv || showHelp;

  if (!started) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          background:
            "linear-gradient(180deg, #1a1410 0%, #2c2218 30%, #3d2e1f 60%, #1a1410 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Courier New', monospace",
          color: "#C4A882",
          overflow: "hidden",
        }}
      >
        <div style={{ textAlign: "center", padding: "0 20px" }}>
          <div
            style={{
              fontSize: 14,
              letterSpacing: 8,
              textTransform: "uppercase",
              color: "#8B7355",
              marginBottom: 12,
            }}
          >
            — Bienvenue à la —
          </div>
          <h1
            style={{
              fontSize: "clamp(32px,6vw,64px)",
              fontWeight: "bold",
              margin: "0 0 8px",
              textShadow: "0 0 40px rgba(196,168,130,0.3)",
              letterSpacing: 3,
              color: "#E8D5B7",
              fontFamily: "Georgia,serif",
            }}
          >
            CIMENTERIE
          </h1>
          <div
            style={{
              fontSize: "clamp(18px,3vw,28px)",
              color: "#A08B6E",
              fontStyle: "italic",
              marginBottom: 40,
            }}
          >
            Abandonnée
          </div>
          <div
            style={{
              maxWidth: 500,
              margin: "0 auto 40px",
              padding: 24,
              lineHeight: 1.8,
              borderTop: "1px solid #4A3D2E",
              borderBottom: "1px solid #4A3D2E",
              fontSize: 14,
              color: "#9E8B6E",
            }}
          >
            Explorez une cimenterie en ruines. Récupérez les anciennes machines
            et matériaux. Transformez-les en créations visibles dans l'usine
            pour attirer des visiteurs et redonner vie à ce lieu oublié.
          </div>
          <button
            onClick={() => setStarted(true)}
            style={{
              padding: "16px 48px",
              fontSize: 18,
              fontFamily: "Georgia,serif",
              background: "transparent",
              border: "2px solid #8B7355",
              color: "#E8D5B7",
              cursor: "pointer",
              letterSpacing: 4,
              textTransform: "uppercase",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#8B7355";
              e.target.style.color = "#1a1410";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = "#E8D5B7";
            }}
          >
            Explorer
          </button>
          <div
            style={{
              marginTop: 32,
              fontSize: 12,
              color: "#6B5D4A",
              letterSpacing: 2,
            }}
          >
            ZQSD — Déplacer &nbsp;|&nbsp; Cliquer-Glisser — Regarder
            &nbsp;|&nbsp; E — Ramasser
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "#000",
        fontFamily: "'Courier New', monospace",
        cursor: "crosshair",
        userSelect: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: "block", width: "100%", height: "100%" }}
        />
      </div>

      {/* Crosshair */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 24,
          height: 24,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            width: "100%",
            height: 1,
            background: "rgba(255,255,255,0.5)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            width: 1,
            height: "100%",
            background: "rgba(255,255,255,0.5)",
          }}
        />
      </div>

      {/* HUD */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: "12px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)",
          pointerEvents: "none",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: "bold",
              marginBottom: 4,
              letterSpacing: 2,
              color: "#E8D5B7",
            }}
          >
            🏭 CIMENTERIE
          </div>
          <div style={{ color: "#A08B6E", fontSize: 13 }}>
            Rénovation: {renovationLevel}%
          </div>
          <div
            style={{
              marginTop: 4,
              height: 6,
              width: 160,
              background: "rgba(255,255,255,0.1)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${renovationLevel}%`,
                background: `linear-gradient(90deg,#8B7355,${
                  renovationLevel > 50 ? "#44AA44" : "#CC8844"
                })`,
                transition: "width 0.5s",
                borderRadius: 3,
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          {[
            { i: "👥", v: visitors, l: "Visiteurs" },
            { i: "🔧", v: invCount, l: "Matériaux" },
            { i: "✨", v: craftedCount, l: "Créations" },
          ].map((s) => (
            <div
              key={s.l}
              style={{ textAlign: "center", color: "#E8D5B7", fontSize: 13 }}
            >
              <div style={{ fontSize: 18 }}>{s.i}</div>
              <div>{s.v}</div>
              <div style={{ fontSize: 10, color: "#8B7355" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 8,
          zIndex: 10,
        }}
      >
        {[
          { k: "I", l: "Inventaire", a: () => setShowInv((p) => !p) },
          { k: "C", l: "Atelier", a: () => setShowCraft((p) => !p) },
          { k: "H", l: "Aide", a: () => setShowHelp((p) => !p) },
          { k: "F", l: "Plein Écran", a: toggleFullscreen },
        ].map((b) => (
          <button
            key={b.k}
            onClick={b.a}
            style={{
              padding: "8px 14px",
              background: "rgba(0,0,0,0.75)",
              border: "1px solid #4A3D2E",
              color: "#C4A882",
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "'Courier New',monospace",
            }}
          >
            <span style={{ color: "#FFD700", fontWeight: "bold" }}>
              [{b.k}]
            </span>{" "}
            {b.l}
          </button>
        ))}
      </div>

      {/* Near collectible */}
      {nearItem && !anyPanel && (
        <div
          style={{
            position: "absolute",
            bottom: 65,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 24px",
            background: "rgba(0,0,0,0.88)",
            border: "1px solid #8B7355",
            color: "#E8D5B7",
            fontSize: 14,
            textAlign: "center",
            maxWidth: "90vw",
            zIndex: 10,
          }}
        >
          <div>
            {ITEMS_DB[nearItem]?.icon}{" "}
            <strong>{ITEMS_DB[nearItem]?.name}</strong>
          </div>
          <div style={{ fontSize: 11, color: "#8B7355", marginTop: 2 }}>
            {ITEMS_DB[nearItem]?.desc}
          </div>
          <button
            onClick={doCollect}
            style={{
              marginTop: 8,
              padding: "6px 20px",
              background: "#8B7355",
              border: "none",
              color: "#1a1410",
              fontWeight: "bold",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "'Courier New',monospace",
            }}
          >
            [E] Ramasser
          </button>
        </div>
      )}

      {/* Near crafted item info */}
      {nearCraft && !nearItem && !anyPanel && (
        <div
          style={{
            position: "absolute",
            bottom: 65,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 24px",
            background: "rgba(0,0,0,0.88)",
            border: "1px solid #44FF88",
            color: "#E8D5B7",
            fontSize: 14,
            textAlign: "center",
            maxWidth: "90vw",
            zIndex: 10,
          }}
        >
          <div>
            {CRAFTS_DB[nearCraft]?.icon}{" "}
            <strong>{CRAFTS_DB[nearCraft]?.name}</strong>
          </div>
          <div style={{ fontSize: 11, color: "#44FF88", marginTop: 2 }}>
            {CRAFTS_DB[nearCraft]?.desc}
          </div>
          <div style={{ fontSize: 11, color: "#FFD700", marginTop: 4 }}>
            +{CRAFTS_DB[nearCraft]?.visitors} visiteurs attirés
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          style={{
            position: "absolute",
            top: 100,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 28px",
            border: "1px solid",
            color: "#E8D5B7",
            fontSize: 14,
            fontWeight: "bold",
            zIndex: 20,
            borderColor:
              notification.type === "collect"
                ? "#44AA44"
                : notification.type === "craft"
                ? "#FFD700"
                : "#888",
            background:
              notification.type === "collect"
                ? "rgba(34,80,34,0.9)"
                : notification.type === "craft"
                ? "rgba(80,60,20,0.9)"
                : "rgba(0,0,0,0.85)",
          }}
        >
          {notification.msg}
        </div>
      )}

      {/* Hint */}
      {!anyPanel && !nearItem && !nearCraft && (
        <div
          style={{
            position: "absolute",
            bottom: 50,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255,255,255,0.35)",
            fontSize: 11,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          Cliquer-glisser pour regarder — ZQSD pour se déplacer
        </div>
      )}

      {/* INVENTORY */}
      {showInv && (
        <div
          data-panel="true"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: "min(420px,90vw)",
            maxHeight: "70vh",
            background: "rgba(20,16,12,0.96)",
            border: "1px solid #4A3D2E",
            padding: 24,
            overflowY: "auto",
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
              color: "#E8D5B7",
              fontSize: 18,
              letterSpacing: 3,
            }}
          >
            <span>🎒 INVENTAIRE</span>
            <button
              onClick={() => setShowInv(false)}
              style={{
                background: "none",
                border: "none",
                color: "#8B7355",
                fontSize: 20,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
          {Object.keys(inventory).length === 0 ? (
            <div
              style={{
                color: "#6B5D4A",
                textAlign: "center",
                padding: 20,
                fontSize: 13,
              }}
            >
              Votre inventaire est vide. Explorez l'usine !
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {Object.entries(inventory).map(([id, count]) => (
                <div
                  key={id}
                  style={{
                    padding: 12,
                    background: "rgba(74,61,46,0.3)",
                    border: "1px solid #3A2E20",
                  }}
                >
                  <div style={{ fontSize: 24 }}>{ITEMS_DB[id]?.icon}</div>
                  <div
                    style={{
                      color: "#C4A882",
                      fontSize: 13,
                      fontWeight: "bold",
                    }}
                  >
                    {ITEMS_DB[id]?.name}
                  </div>
                  <div style={{ color: "#8B7355", fontSize: 11 }}>×{count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CRAFT */}
      {showCraft && (
        <div
          data-panel="true"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: "min(520px,92vw)",
            maxHeight: "80vh",
            background: "rgba(20,16,12,0.96)",
            border: "1px solid #4A3D2E",
            padding: 24,
            overflowY: "auto",
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
              color: "#E8D5B7",
              fontSize: 18,
              letterSpacing: 3,
            }}
          >
            <span>🔨 ATELIER DE RECYCLAGE</span>
            <button
              onClick={() => setShowCraft(false)}
              style={{
                background: "none",
                border: "none",
                color: "#8B7355",
                fontSize: 20,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.entries(CRAFTS_DB).map(([id, r]) => {
              const can = r.needs.every((n) => (inventory[n] || 0) > 0);
              const done = (crafted[id] || 0) > 0;
              return (
                <div
                  key={id}
                  style={{
                    padding: "12px 16px",
                    border: `1px solid ${can ? "#8B7355" : "#2A2218"}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: done
                      ? "rgba(34,80,34,0.2)"
                      : "rgba(74,61,46,0.2)",
                    opacity: can || done ? 1 : 0.5,
                  }}
                >
                  <div
                    style={{ fontSize: 28, minWidth: 36, textAlign: "center" }}
                  >
                    {r.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        color: "#E8D5B7",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {r.name}{" "}
                      {done && (
                        <span style={{ color: "#44AA44" }}>
                          ✓ installé dans l'usine
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#8B7355", fontSize: 11 }}>
                      {r.desc}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        marginTop: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      {r.needs.map((n, i) => {
                        const has = (inventory[n] || 0) > 0;
                        return (
                          <span
                            key={i}
                            style={{
                              padding: "2px 8px",
                              fontSize: 10,
                              background: has
                                ? "rgba(34,80,34,0.4)"
                                : "rgba(80,20,20,0.4)",
                              border: `1px solid ${
                                has ? "#44AA44" : "#663333"
                              }`,
                              color: has ? "#88CC88" : "#AA6666",
                            }}
                          >
                            {ITEMS_DB[n]?.icon} {ITEMS_DB[n]?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 70 }}>
                    <div style={{ color: "#FFD700", fontSize: 11 }}>
                      +{r.visitors} 👥
                    </div>
                    {done ? (
                      <div
                        style={{
                          marginTop: 4,
                          padding: "4px 10px",
                          fontSize: 10,
                          color: "#44AA44",
                          border: "1px solid #336633",
                          display: "inline-block",
                        }}
                      >
                        ✓ Visible
                      </div>
                    ) : (
                      <button
                        onClick={() => can && craftItem(id)}
                        disabled={!can}
                        style={{
                          marginTop: 4,
                          padding: "4px 12px",
                          fontSize: 11,
                          fontWeight: "bold",
                          background: can ? "#8B7355" : "transparent",
                          border: `1px solid ${can ? "#8B7355" : "#3A2E20"}`,
                          color: can ? "#1a1410" : "#4A3D2E",
                          cursor: can ? "pointer" : "default",
                          fontFamily: "'Courier New',monospace",
                        }}
                      >
                        Créer
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* HELP */}
      {showHelp && (
        <div
          data-panel="true"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: "min(380px,90vw)",
            background: "rgba(20,16,12,0.96)",
            border: "1px solid #4A3D2E",
            padding: 24,
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              color: "#E8D5B7",
              fontSize: 18,
              letterSpacing: 3,
            }}
          >
            <span>📖 GUIDE</span>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: "none",
                border: "none",
                color: "#8B7355",
                fontSize: 20,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ color: "#A08B6E", fontSize: 13, lineHeight: 2.2 }}>
            <div>
              <span style={{ color: "#FFD700" }}>ZQSD / WASD</span> — Se
              déplacer
            </div>
            <div>
              <span style={{ color: "#FFD700" }}>Cliquer-Glisser</span> —
              Regarder autour
            </div>
            <div>
              <span style={{ color: "#FFD700" }}>Shift</span> — Courir
            </div>
            <div>
              <span style={{ color: "#FFD700" }}>E</span> — Ramasser un objet
            </div>
            <div>
              <span style={{ color: "#FFD700" }}>I</span> — Inventaire
            </div>
            <div>
              <span style={{ color: "#FFD700" }}>C</span> — Atelier de recyclage
            </div>
            <div
              style={{
                borderTop: "1px solid #3A2E20",
                marginTop: 12,
                paddingTop: 12,
                color: "#8B7355",
                fontSize: 12,
              }}
            >
              Trouvez les orbes lumineux, ramassez-les, puis combinez vos
              matériaux dans l'Atelier. Chaque création apparaît dans l'usine
              (repérez les cercles verts au sol) et attire des visiteurs !
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
